/**
 * RENDER — lighting + procedural environment.
 *
 * Cheap, mobile-friendly lighting (one directional key + one hemisphere fill) and
 * a procedurally generated gradient environment map. The env map is the trick that
 * makes the single chrome marble read as expensive metal at near-zero per-frame
 * cost: it's built once, reused, and disposed with the scene.
 *
 * Owns its GPU resources (textures, render targets) and frees them in `dispose()`.
 */

import * as THREE from 'three';
import { PALETTE } from './theme';

export class Environment {
  readonly directional: THREE.DirectionalLight;
  readonly hemisphere: THREE.HemisphereLight;
  readonly ambient: THREE.AmbientLight;

  /** The generated environment map applied to the scene for reflections. */
  private envMap: THREE.Texture | null = null;
  private pmrem: THREE.PMREMGenerator | null = null;
  private sourceTexture: THREE.Texture | null = null;
  private envTarget: THREE.WebGLRenderTarget | null = null;

  constructor() {
    // Cool white key light from high front-side; gives the chrome a moving highlight.
    this.directional = new THREE.DirectionalLight(0xdfeaff, 2.2);
    this.directional.position.set(6, 12, 4);

    // Hemisphere fill so the dark track keeps subtle shape and shadows aren't black.
    this.hemisphere = new THREE.HemisphereLight(PALETTE.sky, PALETTE.ground, 0.9);

    // A touch of flat ambient to lift the emissive course out of the void.
    this.ambient = new THREE.AmbientLight(0x223044, 0.35);
  }

  /** Add lights to a scene. */
  attach(scene: THREE.Scene): void {
    scene.add(this.directional);
    scene.add(this.directional.target);
    scene.add(this.hemisphere);
    scene.add(this.ambient);
  }

  /**
   * Build a gradient env map (sky → horizon → floor, tinted with course neons) and
   * apply it as `scene.environment` so the marble reflects a coherent world. Uses
   * the renderer's PMREM generator for correct roughness-aware reflections. Safe to
   * call once after the renderer exists; replaces any previous map.
   */
  buildEnvironment(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void {
    this.disposeEnvMap();

    this.sourceTexture = makeGradientEnvTexture();
    this.pmrem = new THREE.PMREMGenerator(renderer);
    this.pmrem.compileEquirectangularShader();
    this.envTarget = this.pmrem.fromEquirectangular(this.sourceTexture);
    this.envMap = this.envTarget.texture;
    scene.environment = this.envMap;

    // The PMREM source is no longer needed once the prefiltered map exists.
    this.sourceTexture.dispose();
    this.sourceTexture = null;
    this.pmrem.dispose();
    this.pmrem = null;
  }

  private disposeEnvMap(): void {
    if (this.envTarget) {
      // Disposing the render target frees its framebuffer AND its `.texture`
      // (== this.envMap), so we null envMap without a second dispose.
      this.envTarget.dispose();
      this.envTarget = null;
      this.envMap = null;
    } else if (this.envMap) {
      this.envMap.dispose();
      this.envMap = null;
    }
    if (this.sourceTexture) {
      this.sourceTexture.dispose();
      this.sourceTexture = null;
    }
    if (this.pmrem) {
      this.pmrem.dispose();
      this.pmrem = null;
    }
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.directional);
    scene.remove(this.directional.target);
    scene.remove(this.hemisphere);
    scene.remove(this.ambient);
    this.directional.dispose();
    this.hemisphere.dispose();
    this.ambient.dispose();
    if (scene.environment === this.envMap) scene.environment = null;
    this.disposeEnvMap();
  }
}

/**
 * Draw a vertical gradient (deep sky → horizon glow → dark floor) into a canvas
 * and wrap it as an equirectangular texture. A couple of soft neon horizon bands
 * give the chrome marble something colorful to catch. Cheap: 256×128, built once.
 */
function makeGradientEnvTexture(): THREE.Texture {
  const w = 256;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Headless / no-2d-context fallback: a flat dark texture still works.
    const tex = new THREE.DataTexture(
      new Uint8Array([11, 14, 20, 255]),
      1,
      1,
      THREE.RGBAFormat,
    );
    tex.needsUpdate = true;
    tex.mapping = THREE.EquirectangularReflectionMapping;
    return tex;
  }

  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0.0, '#10182a'); // upper sky
  grad.addColorStop(0.42, '#0d1320'); // mid
  grad.addColorStop(0.5, '#16263a'); // horizon lift
  grad.addColorStop(0.5, '#0b0e14'); // hard horizon line into floor
  grad.addColorStop(1.0, '#05070b'); // dark floor
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Two faint neon horizon glows so the chrome picks up cyan/violet rim color.
  paintHorizonGlow(ctx, w, h, h * 0.5, 'rgba(45,226,230,0.18)');
  paintHorizonGlow(ctx, w, h, h * 0.46, 'rgba(124,92,255,0.12)');

  const tex = new THREE.CanvasTexture(canvas);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function paintHorizonGlow(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  y: number,
  color: string,
): void {
  const band = ctx.createLinearGradient(0, y - h * 0.12, 0, y + h * 0.12);
  band.addColorStop(0, 'rgba(0,0,0,0)');
  band.addColorStop(0.5, color);
  band.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = band;
  ctx.fillRect(0, y - h * 0.12, w, h * 0.24);
}
